import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { JobsPanelState, JobsStatus, jobStatusToString } from "../../../constants/Jobs";
import { JobContext, UserContext, VersionContext } from "../../../Context";
import { triggerAlertsRefresh } from "../../../store/triggersSlice";
import {
    allJobObjectsAreValid,
    applyValidationResults,
    getJobRelatedObjects,
    jobValidationInProgress,
    objectsShouldValidate,
    updateJobAndSendToQA1,
    updateJobDescription,
} from "../../../utils/jobsUtils";
import { isValidJobDescription, MAX_JOB_DESCRIPTION_LENGTH } from "../../../utils/jobDescriptionUtils";
import InputField from "../../genericComponents/InputField/InputField";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import "./JobDetails.css";

import ApproveIcon from "../../../assets/jobs/approve.svg?react";
import CheckPinIcon from "../../../assets/jobs/checkPin.svg?react";
import EditIcon from "../../../assets/versionControl/pencil.svg?react";

import { formatDate } from "@arcgis/core/intl";
import { StoreState } from "../../../store/rootReducer";
import ConfirmationPopover from "../../genericComponents/confirmationPopover/confirmationPopover";
import { FeatureWithChanges, findObjectsConnectedToPlan } from "../../../utils/newVersioningUtils";
import EditedFeaturesForPlan from "../EditedFeatures/EditedFeaturesForPlan";
import { isDefaultVersion } from "../../../utils/versioningUtils";
import Collapsible from "../../genericComponents/Collapsible/Collapsible";
import type Graphic from "@arcgis/core/Graphic";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface JobPlanRejectedDetailsProps {
    jobObject: Graphic;
    setJobPanelState: (state: JobsPanelState) => void;
    setLoading: (loading: boolean) => void;
    storeHighlightHandle: (handle: ResourceHandle | null) => void;
    clearHighlightedFeatures: () => void;
}

const JobPlanRejectedDetails: React.FC<JobPlanRejectedDetailsProps> = ({
    jobObject,
    setJobPanelState,
    setLoading,
    storeHighlightHandle,
    clearHighlightedFeatures,
}) => {
    const dispatch = useDispatch();

    const [currentJobStep, setCurrentJobStep] = useState<Graphic | null>(null);
    const [previousJobStep, setPreviousJobStep] = useState<Graphic | null>(null);
    const [historicalSteps, setHistoricalSteps] = useState<Graphic[]>([]);

    const [jobFeaturesWithChanges, setJobFeaturesWithChanges] = useState<FeatureWithChanges[]>([]);
    const [isLoadingChanges, setIsLoadingChanges] = useState<boolean>(true);

    const [comment, setComment] = useState<string | undefined>(undefined);

    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [descriptionDraft, setDescriptionDraft] = useState<string>("");
    const [isSavingDescription, setIsSavingDescription] = useState(false);
    // Holds the saved description after an edit so the view reflects it without
    // mutating the read-only jobObject prop or reloading the job list.
    const [descriptionOverride, setDescriptionOverride] = useState<string | undefined>(undefined);
    const description = descriptionOverride ?? jobObject.attributes.beskrivelse;

    const activeVersionGuid = useSelector((state: StoreState) => state.version.activeGuid);
    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);

    const versionContext = useContext(VersionContext);
    const versionManagementRef = versionContext?.versionServiceRef.value;

    const userContext = useContext(UserContext);
    const user = userContext?.user.value;

    const jobsContext = useContext(JobContext);

    const validateObjects = objectsShouldValidate(jobObject.attributes.jobb_type);

    const returnToJobList = () => {
        setJobPanelState(JobsPanelState.LIST);
        jobsContext?.selectedJob.set(null);
    };

    const startEditingDescription = () => {
        setDescriptionDraft(description ?? "");
        setIsEditingDescription(true);
    };

    const cancelEditingDescription = () => {
        setIsEditingDescription(false);
    };

    const saveDescription = async () => {
        if (!isValidJobDescription(descriptionDraft)) {
            if (descriptionDraft.trim().length === 0) {
                toast.error("Beskrivelse må fylles ut");
            } else {
                toast.error("Beskrivelse kan ikke være lengre enn 255 tegn");
            }
            return;
        }

        setIsSavingDescription(true);
        const success = await updateJobDescription(jobObject.attributes.globalid, descriptionDraft);
        setIsSavingDescription(false);

        if (success) {
            setDescriptionOverride(descriptionDraft);
            toast.success("Beskrivelsen er oppdatert");
            setIsEditingDescription(false);
        } else {
            toast.error("Kunne ikke oppdatere beskrivelsen");
        }
    };

    // EditedFeaturesForPlan renders a per-object validation status; without computing it the
    // panels show "Validerer..." forever. The rejected plan resubmits and posts to DEFAULT,
    // so it validates with the same "PostToDefault" context as the active plan view.
    const getValidationResults = async (objects: FeatureWithChanges[]) => {
        const versionName = activeVersion?.versionIdentifier.name ?? null;
        await applyValidationResults(objects, setJobFeaturesWithChanges, versionName, "PostToDefault");
    };

    const validationInProgress = () => jobValidationInProgress(jobFeaturesWithChanges);

    const objectsAreValid = () => allJobObjectsAreValid(jobFeaturesWithChanges, validateObjects);

    const checkIfDefaultVersion = () => {
        if (versionManagementRef?.current && activeVersion) {
            return isDefaultVersion(activeVersion, versionManagementRef.current);
        }
    };

    const sendUpdatedJobToQA1 = async () => {
        setLoading(true);
        if (!versionManagementRef?.current.loaded) {
            await versionManagementRef?.current.load();
        }
        if ("{" + activeVersionGuid + "}" === versionManagementRef?.current.defaultVersionIdentifier.guid) {
            toast.error("Kan ikke oppdatere jobb i default versjon. Velg en annen versjon.");
            setLoading(false);
            return;
        }
        if (jobObject) {
            const success = await updateJobAndSendToQA1(
                jobFeaturesWithChanges,
                currentJobStep?.attributes.globalid,
                comment ?? "",
                jobObject.attributes.globalid,
                jobObject.attributes.objectid,
                jobObject.attributes.navn,
                activeVersionGuid,
                activeVersion?.versionIdentifier.name ?? "sde.DEFAULT",
                user?.username ?? ""
            );

            if (success) {
                toast.success("Jobben er sendt til QA1 for godkjenning");
                dispatch(triggerAlertsRefresh());
                returnToJobList();
            }
        }
        setLoading(false);
    };

    const canIReview = () => {
        if (
            jobObject.attributes.status === JobsStatus.PlanIkkeGodkjent &&
            jobObject.attributes.opprettet_av === user?.username
        ) {
            return true;
        } else {
            return false;
        }
    };

    const getObjectsConnectedToJob = async () => {
        const job = jobObject;
        const versionName = activeVersion?.versionIdentifier.name;
        const versionGuid = activeVersionGuid;

        if (job && versionName && versionGuid) {
            setIsLoadingChanges(true);
            // A rejected plan has already been posted to DEFAULT, so the version initially
            // holds no pending diffs — findDifferencesForPlan (which filters to changed
            // objects) would return an empty list. findObjectsConnectedToPlan keeps every
            // job-connected object so the user can see the plan's objects, while still
            // gathering the version's many:many relationship rows and deletes once the user
            // edits an object, so the resubmit to QA1 posts a complete combined object.
            const objects = await findObjectsConnectedToPlan(job, versionGuid, versionName);
            console.log("Objects with changes from job:", objects);
            setJobFeaturesWithChanges(objects);
            setIsLoadingChanges(false);
            if (validateObjects) {
                getValidationResults(objects); // fire and forget
            }
        }
    };

    useEffect(() => {
        getJobRelatedObjects(jobObject.attributes.objectid, "Steg").then((objects) => {
            // Find step before this step
            const orderedSteps = [...objects];
            // Sorts steps by opprettet_dato in descending order in case there are multiple steps with the same jobb_status
            orderedSteps.sort((a, b) => b.attributes.opprettet_dato - a.attributes.opprettet_dato);
            let activeStep;
            for (let step of orderedSteps) {
                if (step.attributes.jobb_status === jobObject.attributes.status) {
                    activeStep = step;
                    break;
                }
            }
            if (activeStep) {
                setCurrentJobStep(activeStep);
                // Find step with highest opprettet_dato less than or equal to activeStep
                // but with a different status(could be same timestamp but different status)
                const maxTime = activeStep.attributes.opprettet_dato;
                let previousStep = null;

                // Sort steps by opprettet_dato in descending order

                for (let step of orderedSteps) {
                    if (
                        step.attributes.globalid !== activeStep.attributes.globalid &&
                        step.attributes.opprettet_dato <= maxTime &&
                        step.attributes.jobb_status !== activeStep.attributes.jobb_status
                    ) {
                        previousStep = step;
                        break;
                    }
                }
                setPreviousJobStep(previousStep);

                setHistoricalSteps(() => {
                    if (jobObject.attributes.status === JobsStatus.Ferdig) {
                        return orderedSteps;
                    } else {
                        return orderedSteps.filter(
                            (step) => step.attributes.globalid !== activeStep.attributes.globalid
                        );
                    }
                });
            }
        });
    }, [jobObject]);

    useEffect(() => {
        getObjectsConnectedToJob();
    }, []);

    return (
        <>
            <div className="job-details-body">
                <div className="job-details-body-content">
                    <label className="job-details-label">
                        <span className="hidden">{jobObject.attributes.globalid}</span>
                        <span>Jobbnavn</span>
                        <span>{jobObject.attributes.navn}</span>
                    </label>
                    {isEditingDescription ? (
                        <div className="job-details-label">
                            <InputField
                                label="Beskrivelse"
                                onChange={setDescriptionDraft}
                                inputFieldId={jobObject.attributes.globalid + "description"}
                                value={descriptionDraft}
                                type="text"
                                isTextArea
                                textAreaRows={3}
                                maxLength={MAX_JOB_DESCRIPTION_LENGTH}
                                disabled={isSavingDescription}
                            />
                            <div className="flex-row small-gap" style={{ marginTop: "4px" }}>
                                <NfsButton disabled={isSavingDescription} onClick={saveDescription}>
                                    Lagre
                                </NfsButton>
                                <NfsButton outlined disabled={isSavingDescription} onClick={cancelEditingDescription}>
                                    Avbryt
                                </NfsButton>
                            </div>
                        </div>
                    ) : (
                        <label className="job-details-label">
                            <div className="flex-row space-between center-h">
                                <span>Beskrivelse</span>
                                {canIReview() && !checkIfDefaultVersion() && (
                                    <button
                                        type="button"
                                        className="icon-button"
                                        aria-label="Rediger beskrivelse"
                                        onClick={startEditingDescription}
                                    >
                                        <EditIcon />
                                    </button>
                                )}
                            </div>
                            <span>{description}</span>
                        </label>
                    )}
                    {previousJobStep && (
                        <label className="job-details-label">
                            <span>Kommentar fra QA</span>
                            <span>{previousJobStep.attributes.kommentar}</span>
                        </label>
                    )}
                    {canIReview() && (
                        <InputField
                            label="Kommentar til QA1"
                            onChange={setComment}
                            inputFieldId={jobObject.attributes.globalid + "comment"}
                            value={comment}
                            type="text"
                            isTextArea
                            textAreaRows={3}
                            maxLength={255}
                        />
                    )}
                    {historicalSteps.length > 0 && (
                        <Collapsible
                            isOpen={true}
                            setIsOpen={() => {}}
                            title={`Steg med kommentarer (${historicalSteps.length})`}
                            titleClassName="input-field-label"
                        >
                            {historicalSteps.map((step) => (
                                <label key={step.attributes.globalid} className="job-step-container">
                                    <div className="job-step-header">
                                        <span>
                                            <strong>{jobStatusToString(step.attributes.jobb_status)}</strong>
                                        </span>
                                        <span>
                                            {step.attributes.fullfort_av} ({formatDate(step.attributes.oppdatert_dato)})
                                        </span>
                                    </div>
                                    <span>{step.attributes.kommentar ?? "[ingen kommentar]"}</span>
                                </label>
                            ))}
                        </Collapsible>
                    )}
                    <div className="empty-button flex-row small-gap center">
                        <CheckPinIcon />
                        {`Objekter tilknyttet jobben (${jobFeaturesWithChanges.length})`}
                    </div>
                    <div className="flex-row space-between">
                        <span className="job-card-key">Eier</span>
                        <span className="job-card-value bold">{jobObject.attributes.opprettet_av}</span>
                    </div>
                    <div className="flex-row space-between">
                        <span className="job-card-key">Oppdatert</span>
                        <span className="job-card-value bold">{formatDate(jobObject.attributes.oppdatert_dato)}</span>
                    </div>
                </div>
                <EditedFeaturesForPlan
                    featuresAddedToJob={jobFeaturesWithChanges}
                    refreshFeaturesAddedToJob={getObjectsConnectedToJob}
                    isLoadingChanges={isLoadingChanges}
                    storeHighlightHandle={storeHighlightHandle}
                    clearHighlightedFeatures={clearHighlightedFeatures}
                />
            </div>
            {canIReview() && !checkIfDefaultVersion() && (
                <div className="job-details-actions">
                    {validateObjects && validationInProgress() && (
                        <span className="job-details-actions-info">Validerer objekter tilknyttet jobben...</span>
                    )}
                    {validateObjects && !validationInProgress() && !objectsAreValid() && (
                        <span className="job-details-actions-warning">
                            Noen objekter har valideringsfeil. Rett feilene før du sender jobben videre.
                        </span>
                    )}
                    {isEditingDescription && (
                        <span className="job-details-actions-info">
                            Lagre eller avbryt beskrivelsen før du sender jobben.
                        </span>
                    )}
                    <div className="job-details-actions-buttons">
                        <ConfirmationPopover
                            message="Bekreft at du ønsker å oppdatere jobben og sende den til QA1"
                            onConfirm={sendUpdatedJobToQA1}
                            title="Send til QA1"
                            confirmText="Bekreft"
                            cancelText="Avbryt"
                            onCancel={() => {}}
                            buttonStyle={{ width: "50%", marginLeft: "auto" }}
                            disabled={isLoadingChanges || !objectsAreValid() || isEditingDescription}
                        >
                            <div className="nfs-button">
                                <ApproveIcon />
                                Oppdater og send til QA1
                            </div>
                        </ConfirmationPopover>
                    </div>
                </div>
            )}
            {checkIfDefaultVersion() && (
                <div className="job-details-actions">
                    <span>Plan kan kun sendes til QA1 fra versjon</span>
                </div>
            )}
        </>
    );
};

export default JobPlanRejectedDetails;
